import { gql, useQuery } from '@apollo/client'

// Resolves the /participation/:id route param (an 8-char slug OR a full session id) to a valid
// participation-session id. The backend resolver validates BOTH forms — a slug must be the
// session's current slug, and a raw session id is only valid if the session was never slug-managed
// (once a session has a slug, the old-style id link is disabled too). So we always ask the server
// rather than passing session-id links through.
const PARTICIPATION_SESSION_ID_BY_SLUG = gql`
  query ParticipationSessionIdBySlug($decisionId: ID!, $slug: String!) {
    participationSessionIdBySlug(decisionId: $decisionId, slug: $slug)
  }
`

interface ResolvedParticipationSessionId {
  /** The resolved participation-session id, or undefined while loading / when the link is invalid. */
  participationSessionId?: string
  loading: boolean
  /** True when the link didn't resolve — unknown, rotated, or an old-style id link that's now disabled. */
  notFound: boolean
}

/**
 * Resolves a /participation/:id route param to a participation-session id, or reports notFound when
 * the link is no longer valid. Works both authenticated (facilitator) and unauthenticated (ad-hoc
 * participant) — the Apollo client routes to the auth/unauth endpoint automatically.
 */
export function useResolvedParticipationSessionId(
  decisionId: string,
  idOrSlug: string
): ResolvedParticipationSessionId {
  // network-only so a link that was rotated in another session isn't served as valid from cache.
  const { data, loading } = useQuery(PARTICIPATION_SESSION_ID_BY_SLUG, {
    variables: { decisionId, slug: idOrSlug },
    fetchPolicy: 'network-only',
  })

  const resolved = (data?.participationSessionIdBySlug as string | null | undefined) ?? undefined
  return {
    participationSessionId: resolved,
    loading,
    notFound: !loading && !resolved,
  }
}
